import { useState } from "react";
import { Phone, Mail, MapPin, CheckCircle2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CONTACT } from "@/constants/testIds";
import { cn } from "@/lib/utils";

const initial = { name: "", email: "", phone: "", message: "" };

export default function ContactUs() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSuccess(true);
      setForm(initial);
      toast.success("Message sent. We'll respond soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#000000] text-white">
      <section className="py-24 md:py-32 border-b border-white/8 relative">
        <div className="absolute inset-0 grid-lines-navy opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-condensed text-xs font-bold tracking-[0.28em] uppercase text-red-500 mb-3">— Contact Us</p>
          <h1 className="font-display font-black text-4xl md:text-6xl uppercase leading-[1] text-balance max-w-3xl">
            Reach Out to<br />Marco Polo Security<span className="text-red-500">.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-400 leading-relaxed max-w-2xl">
            Questions, quotes, or emergency coverage — our team is here to help across Washington State.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10">
          {/* Info */}
          <div className="space-y-5">
            <InfoCard icon={<Phone className="w-5 h-5 text-red-500" />} label="Phone" value="206-742-9195" href="tel:2067429195" />
            <InfoCard icon={<Mail className="w-5 h-5 text-red-500" />} label="Email" value="info@marcopolosecurity.com" href="mailto:info@marcopolosecurity.com" />
            <InfoCard
              icon={<MapPin className="w-5 h-5 text-red-500" />}
              label="Service Area"
              value="King County, Pierce County & surrounding areas · Washington State"
            />

            <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-6">
              <p className="font-condensed text-xs font-bold tracking-[0.28em] uppercase text-red-500 mb-2">Hours</p>
              <p className="text-white">24/7 emergency coverage available.</p>
              <p className="text-slate-400 text-sm mt-1 font-condensed uppercase tracking-widest">Business office · Mon–Fri · 8:00 AM – 6:00 PM PT</p>
            </div>
          </div>

          {/* Form */}
          <div className="bg-[#0A0A0A]/80 backdrop-blur-xl border border-white/10 rounded-sm p-6 sm:p-8">
            {success ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 rounded-sm bg-red-600 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display font-black text-2xl uppercase">Message Received.</h3>
                <p className="mt-3 text-slate-400">We'll get back to you shortly.</p>
                <button
                  onClick={() => setSuccess(false)}
                  className="mt-6 font-condensed text-sm uppercase tracking-widest text-red-500 hover:text-red-400 border-b border-red-600"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <Field label="Name" required>
                  <input required value={form.name} onChange={upd("name")} data-testid={CONTACT.name} className={inputCls} />
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Email" required>
                    <input required type="email" value={form.email} onChange={upd("email")} data-testid={CONTACT.email} className={inputCls} />
                  </Field>
                  <Field label="Phone">
                    <input value={form.phone} onChange={upd("phone")} data-testid={CONTACT.phone} className={inputCls} />
                  </Field>
                </div>
                <Field label="Message" required>
                  <textarea
                    required rows={5} value={form.message} onChange={upd("message")}
                    data-testid={CONTACT.message}
                    className={cn(inputCls, "resize-none")}
                    placeholder="How can we help?"
                  />
                </Field>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid={CONTACT.submit}
                  className="group w-full bg-white hover:bg-slate-100 disabled:opacity-60 text-[#0A0A0A] px-6 py-3.5 rounded-sm font-condensed uppercase tracking-wider font-bold transition-colors flex items-center justify-center gap-2"
                >
                  {submitting ? "Sending…" : (<>Send Message <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></>)}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoCard({ icon, label, value, href }) {
  const content = (
    <div className="group bg-[#0A0A0A] border border-white/10 rounded-sm p-6 flex items-start gap-4 hover:border-red-500/50 transition-colors">
      <div className="w-11 h-11 rounded-sm bg-white/5 border border-white/10 flex items-center justify-center shrink-0 group-hover:bg-red-600 group-hover:border-red-600 transition-colors">
        {icon}
      </div>
      <div>
        <p className="font-condensed text-xs font-bold tracking-[0.28em] uppercase text-red-500 mb-1">{label}</p>
        <p className="text-white text-base">{value}</p>
      </div>
    </div>
  );
  return href ? <a href={href} className="block">{content}</a> : content;
}

const inputCls =
  "w-full bg-[#000000] border border-white/15 rounded-sm px-4 py-3 h-11 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block font-condensed text-xs font-bold uppercase tracking-widest text-slate-300 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}
